import type { PublicTextNode } from './types';

export type MaipoTextLayoutSource = Pick<PublicTextNode, 'text' | 'frame' | 'style'>;

export interface MaipoTextMeasurer {
  /**
   * Return the untracked advance width for exactly `text` in the source face.
   * The portable layout algorithm adds the declared N - 1 letter-spacing gaps.
   */
  measure(text: string, source: MaipoTextLayoutSource): number;
}

export interface MaipoTextLayoutLine {
  text: string;
  x: number;
  y: number;
  width: number;
}

export interface MaipoTextLayout {
  lines: readonly MaipoTextLayoutLine[];
  width: number;
  height: number;
  contentHeight: number;
  lineHeight: number;
  lineCount: number;
  horizontalOverflow: boolean;
  verticalOverflow: boolean;
  overflow: boolean;
}

function codePointCount(value: string): number {
  return Array.from(value).length;
}

function measured(text: string, source: MaipoTextLayoutSource, measurer: MaipoTextMeasurer): number {
  const advance = measurer.measure(text, source);
  if (!Number.isFinite(advance) || advance < 0) throw new Error(`Text measurer returned invalid width ${String(advance)} for ${JSON.stringify(text)}.`);
  return advance + Math.max(0, codePointCount(text) - 1) * source.style.letterSpacing;
}

function breakToken(token: string, source: MaipoTextLayoutSource, measurer: MaipoTextMeasurer): string[] {
  const pieces: string[] = [];
  let current = '';
  for (const character of token) {
    if (current && measured(current + character, source, measurer) > source.frame.width) {
      pieces.push(current);
      current = character;
    } else current += character;
  }
  if (current || pieces.length === 0) pieces.push(current);
  return pieces;
}

/**
 * Backend-neutral Maipo 0.1 text layout. It normalizes CRLF/CR to LF, wraps at
 * whitespace opportunities, breaks overlong tokens by Unicode code point,
 * preserves explicit blank lines, applies N - 1 letter-spacing gaps, and clips
 * fixed-height frames to complete lines. Font shaping remains replaceable: the
 * caller supplies exact untracked advance widths for the declared face.
 */
export function layoutMaipoText(source: MaipoTextLayoutSource, measurer: MaipoTextMeasurer): MaipoTextLayout {
  const rawLines: string[] = [];
  for (const paragraph of source.text.replace(/\r\n?/g, '\n').split('\n')) {
    if (paragraph.length === 0) {
      rawLines.push('');
      continue;
    }
    const tokens = paragraph.match(/\S+\s*|\s+/gu) ?? [''];
    let current = '';
    for (const token of tokens) {
      const candidate = current + token;
      if (measured(candidate.trimEnd(), source, measurer) <= source.frame.width) {
        current = candidate;
        continue;
      }
      if (current) rawLines.push(current.trimEnd());
      const next = token.trim();
      if (!next) {
        current = '';
        continue;
      }
      const pieces = breakToken(next, source, measurer);
      rawLines.push(...pieces.slice(0, -1));
      current = pieces.at(-1) ?? '';
    }
    rawLines.push(current.trimEnd());
  }

  const lineHeight = source.style.fontSize * source.style.lineHeight;
  const lineWidths = rawLines.map((line) => measured(line, source, measurer));
  const contentHeight = Math.max(lineHeight, rawLines.length * lineHeight);
  const height = source.frame.autoHeight ? contentHeight : source.frame.height;
  const visibleCount = source.frame.autoHeight ? rawLines.length : Math.max(0, Math.floor(source.frame.height / lineHeight));
  const lines = rawLines.slice(0, visibleCount).map((text, index) => {
    const width = lineWidths[index] ?? 0;
    const x = source.style.textAlign === 'center'
      ? (source.frame.width - width) / 2
      : source.style.textAlign === 'right' ? source.frame.width - width : 0;
    return { text, x, y: index * lineHeight, width };
  });
  const horizontalOverflow = lineWidths.some((width) => width > source.frame.width);
  const verticalOverflow = !source.frame.autoHeight && contentHeight > source.frame.height;
  return {
    lines,
    width: source.frame.width,
    height,
    contentHeight,
    lineHeight,
    lineCount: rawLines.length,
    horizontalOverflow,
    verticalOverflow,
    overflow: horizontalOverflow || verticalOverflow,
  };
}
